import type { MapPing, HoveredPing } from '../types';
import type { MapProjectionHandle } from './types';
import { DEVIATION_COLOR, BREADCRUMB_COLOR } from '../constants';
import { pointToPolylineDistance, DEVIATION_THRESHOLD_METERS } from '@utils/geoUtils';

// TYPES --------------------------------------------------------------------- //

interface PingOverlayResult {
  deviationLines: google.maps.Polyline[];
  deviationDots: google.maps.Marker[];
  routePingMarkers: google.maps.Marker[];
}

interface OnRouteWindow {
  /** Ping must be at or after this time to be checked for off-route (e.g. pickup_successful). */
  start?: string;
  /** Ping must be before this time to be checked for off-route (e.g. delivery_successful). Open-ended if omitted. */
  end?: string;
}

interface CreatePingOverlaysOptions {
  map: google.maps.Map;
  movePings: MapPing[];
  moveId: number;
  routePath: google.maps.LatLngLiteral[];
  projectionHandle: MapProjectionHandle | null;
  onHoverPing?: (ping: HoveredPing | null) => void;
  hoverListeners: google.maps.MapsEventListener[];
  /** Time window during which the driver is expected to be on route. Pings outside this window are never flagged as off-route. */
  onRouteWindow?: OnRouteWindow;
}

interface CreateBreadcrumbDotsOptions {
  map: google.maps.Map;
  unassignedPings: MapPing[];
  projectionHandle: MapProjectionHandle | null;
  onHoverPing?: (ping: HoveredPing | null) => void;
  hoverListeners: google.maps.MapsEventListener[];
}

// HELPERS ------------------------------------------------------------------- //

function addPingHoverListeners(
  marker: google.maps.Marker,
  loc: MapPing,
  moveId: number | null,
  projectionHandle: MapProjectionHandle | null,
  onHoverPing: (ping: HoveredPing | null) => void,
  hoverListeners: google.maps.MapsEventListener[]
): void {
  const enterListener = marker.addListener('mouseover', () => {
    const pos = marker.getPosition();
    if (!pos) return;
    const point = projectionHandle?.toScreenPoint({ lat: pos.lat(), lng: pos.lng() });
    if (!point) return;
    onHoverPing({ location: loc, moveId, screenX: point.x, screenY: point.y });
  });
  const leaveListener = marker.addListener('mouseout', () => {
    onHoverPing(null);
  });
  hoverListeners.push(enterListener, leaveListener);
}

// FACTORY ------------------------------------------------------------------- //

/**
 * Creates deviation lines (off-route pings) + invisible on-route hover markers
 * for a single move's pings along a decoded route path.
 */
export function createPingOverlays({
  map,
  movePings,
  moveId,
  routePath,
  projectionHandle,
  onHoverPing,
  hoverListeners,
  onRouteWindow,
}: CreatePingOverlaysOptions): PingOverlayResult {
  const deviationLines: google.maps.Polyline[] = [];
  const deviationDots: google.maps.Marker[] = [];
  const routePingMarkers: google.maps.Marker[] = [];

  if (routePath.length < 2) return { deviationLines, deviationDots, routePingMarkers };

  // Pre-parse window bounds once for the loop
  const windowStart = onRouteWindow?.start ? new Date(onRouteWindow.start).getTime() : null;
  const windowEnd = onRouteWindow?.end ? new Date(onRouteWindow.end).getTime() : null;

  movePings.forEach(loc => {
    if (!loc.location?.coordinates?.length) return;
    const ping = { lat: loc.location.coordinates[1], lng: loc.location.coordinates[0] };
    const { distance, nearest } = pointToPolylineDistance(ping, routePath);

    // Only flag off-route if the ping falls within the on-route expected window.
    // Pings before the window (e.g. driver heading to pickup) are never flagged.
    const pingTime = loc.time ? new Date(loc.time).getTime() : null;
    const inOnRouteWindow =
      pingTime != null &&
      (windowStart == null || pingTime >= windowStart) &&
      (windowEnd == null || pingTime <= windowEnd);

    if (distance > DEVIATION_THRESHOLD_METERS && inOnRouteWindow) {
      // Dashed line from nearest point on route to the off-route ping
      const deviationLine = new google.maps.Polyline({
        path: [nearest, ping],
        geodesic: true,
        strokeColor: DEVIATION_COLOR,
        strokeWeight: 2,
        strokeOpacity: 0,
        icons: [
          {
            icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.7, strokeColor: DEVIATION_COLOR, scale: 1 },
            offset: '0',
            repeat: '8px',
          },
        ],
        zIndex: 6,
        clickable: false,
        map,
      });
      deviationLines.push(deviationLine);

      // Small circle marker at the off-route ping
      const deviationDot = new google.maps.Marker({
        map,
        position: ping,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: DEVIATION_COLOR,
          fillOpacity: 0.8,
          strokeColor: DEVIATION_COLOR,
          strokeWeight: 1,
          scale: 4,
        },
        clickable: !!onHoverPing,
        zIndex: 7,
      });
      if (onHoverPing) {
        addPingHoverListeners(deviationDot, loc, moveId, projectionHandle, onHoverPing, hoverListeners);
      }
      deviationDots.push(deviationDot);
    } else if (onHoverPing) {
      // Invisible marker on the route line — responds to hover only
      const routePingMarker = new google.maps.Marker({
        map,
        position: ping,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillOpacity: 0,
          strokeOpacity: 0,
          scale: 8,
        },
        clickable: true,
        zIndex: 10,
      });
      addPingHoverListeners(routePingMarker, loc, moveId, projectionHandle, onHoverPing, hoverListeners);
      routePingMarkers.push(routePingMarker);
    }
  });

  return { deviationLines, deviationDots, routePingMarkers };
}

/**
 * Creates breadcrumb dots for unassigned pings (between-move GPS pings).
 */
export function createBreadcrumbDots({
  map,
  unassignedPings,
  projectionHandle,
  onHoverPing,
  hoverListeners,
}: CreateBreadcrumbDotsOptions): google.maps.Marker[] {
  const dots: google.maps.Marker[] = [];

  unassignedPings.forEach(loc => {
    if (!loc.location?.coordinates?.length) return;
    const dot = new google.maps.Marker({
      map,
      position: { lat: loc.location.coordinates[1], lng: loc.location.coordinates[0] },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: BREADCRUMB_COLOR,
        fillOpacity: 0.8,
        strokeColor: BREADCRUMB_COLOR,
        strokeOpacity: 0.5,
        strokeWeight: 1,
        scale: 3,
      },
      clickable: !!onHoverPing,
      zIndex: 8,
    });
    if (onHoverPing) {
      addPingHoverListeners(dot, loc, null, projectionHandle, onHoverPing, hoverListeners);
    }
    dots.push(dot);
  });

  return dots;
}
